import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { TrendingUp, Users, MousePointer, BarChart } from 'lucide-react';
import { getABStats } from '../utils/abTest';

export function ABTestDashboard() {
  const [stats, setStats] = useState({ A: { views: 0, conversions: 0 }, B: { views: 0, conversions: 0 } });
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${backendUrl}/api/ab-test/results`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch A/B test results');
        }
        
        const data = await response.json();
        
        // Transform backend data to match component state structure
        setStats({
          A: {
            views: data.variant_a.impressions,
            conversions: data.variant_a.conversions
          },
          B: {
            views: data.variant_b.impressions,
            conversions: data.variant_b.conversions
          }
        });
      } catch (err) {
        console.error('Error fetching A/B test results:', err);
        setError(err.message);
        // Fallback to localStorage if backend fails
        const localStats = getABStats();
        setStats(localStats);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
  }, [refreshKey]);

  const calculateConversionRate = (variant) => {
    if (stats[variant].views === 0) return 0;
    return ((stats[variant].conversions / stats[variant].views) * 100).toFixed(2);
  };

  const getTotalViews = () => stats.A.views + stats.B.views;
  const getTotalConversions = () => stats.A.conversions + stats.B.conversions;

  const getWinner = () => {
    const rateA = parseFloat(calculateConversionRate('A'));
    const rateB = parseFloat(calculateConversionRate('B'));
    
    if (getTotalViews() < 100) return 'Insufficient Data';
    if (rateA > rateB) return 'Variant A';
    if (rateB > rateA) return 'Variant B';
    return 'Tie';
  };

  const clearStats = async () => {
    if (window.confirm('Clear all A/B test statistics? This cannot be undone.')) {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${backendUrl}/api/ab-test/clear`, {
          method: 'DELETE'
        });
        
        if (!response.ok) {
          throw new Error('Failed to clear statistics');
        }
        
        // Also clear localStorage
        localStorage.removeItem('ab_test_stats');
        setRefreshKey(prev => prev + 1);
        alert('Statistics cleared successfully!');
      } catch (err) {
        console.error('Error clearing statistics:', err);
        alert('Failed to clear statistics. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2">A/B Test Dashboard</h1>
            <p className="text-gray-600">Hero Message Testing Results</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => setRefreshKey(prev => prev + 1)} variant="outline" disabled={loading}>
              {loading ? 'Loading...' : 'Refresh Data'}
            </Button>
            <Button onClick={clearStats} variant="destructive" disabled={loading}>
              Clear Stats
            </Button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800">
              ⚠️ {error}. Showing cached data from browser.
            </p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            <p className="mt-4 text-gray-600">Loading A/B test data...</p>
          </div>
        )}

        {!loading && (
          <>
        {/* Test Info */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Test Configuration</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold mb-2">Variant A (Control)</h3>
                <p className="text-gray-700">"You Dream It, We Help You Build It!"</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Variant B (Test)</h3>
                <p className="text-gray-700">"You Dream It, We Help You Write It, Protect It, Publish It, and Sell It!"</p>
              </div>
            </div>
            <div className="mt-4 p-3 bg-blue-50 rounded">
              <p className="text-sm text-blue-900">
                <strong>Note:</strong> Both variants include sub-tagline: "From First Draft to Bestseller - All in One Place"
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Overall Stats */}
        <div className="grid md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Views</p>
                  <p className="text-3xl font-bold">{getTotalViews()}</p>
                </div>
                <Users className="w-10 h-10 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Conversions</p>
                  <p className="text-3xl font-bold">{getTotalConversions()}</p>
                </div>
                <MousePointer className="w-10 h-10 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Overall Rate</p>
                  <p className="text-3xl font-bold">
                    {getTotalViews() > 0 
                      ? ((getTotalConversions() / getTotalViews()) * 100).toFixed(2)
                      : 0
                    }%
                  </p>
                </div>
                <BarChart className="w-10 h-10 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card className={`border-2 ${
            getWinner() === 'Variant A' ? 'border-green-500 bg-green-50' :
            getWinner() === 'Variant B' ? 'border-green-500 bg-green-50' :
            'border-gray-300'
          }`}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Winner</p>
                  <p className="text-2xl font-bold">{getWinner()}</p>
                </div>
                <TrendingUp className="w-10 h-10 text-green-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Variant Comparison */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Variant A */}
          <Card className={calculateConversionRate('A') > calculateConversionRate('B') && getTotalViews() >= 100 ? 'border-2 border-green-500' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Variant A (Control)</span>
                {calculateConversionRate('A') > calculateConversionRate('B') && getTotalViews() >= 100 && (
                  <span className="text-sm bg-green-500 text-white px-2 py-1 rounded">Winner</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="font-semibold text-center text-purple-900">
                    "You Dream It, We Help You Build It!"
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded">
                    <p className="text-sm text-gray-600">Views</p>
                    <p className="text-3xl font-bold">{stats.A.views}</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded">
                    <p className="text-sm text-gray-600">Conversions</p>
                    <p className="text-3xl font-bold">{stats.A.conversions}</p>
                  </div>
                </div>

                <div className="text-center p-6 bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Conversion Rate</p>
                  <p className="text-5xl font-bold text-purple-600">{calculateConversionRate('A')}%</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Variant B */}
          <Card className={calculateConversionRate('B') > calculateConversionRate('A') && getTotalViews() >= 100 ? 'border-2 border-green-500' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Variant B (Test)</span>
                {calculateConversionRate('B') > calculateConversionRate('A') && getTotalViews() >= 100 && (
                  <span className="text-sm bg-green-500 text-white px-2 py-1 rounded">Winner</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-pink-50 p-4 rounded-lg">
                  <p className="font-semibold text-center text-pink-900">
                    "You Dream It, We Help You Write It, Protect It, Publish It, and Sell It!"
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded">
                    <p className="text-sm text-gray-600">Views</p>
                    <p className="text-3xl font-bold">{stats.B.views}</p>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded">
                    <p className="text-sm text-gray-600">Conversions</p>
                    <p className="text-3xl font-bold">{stats.B.conversions}</p>
                  </div>
                </div>

                <div className="text-center p-6 bg-gradient-to-r from-pink-100 to-orange-100 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Conversion Rate</p>
                  <p className="text-5xl font-bold text-pink-600">{calculateConversionRate('B')}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tips */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Testing Tips</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• <strong>Minimum Sample:</strong> At least 100 views per variant before declaring winner</li>
              <li>• <strong>Statistical Significance:</strong> Ideally 1000+ views for reliable results</li>
              <li>• <strong>Testing URL:</strong> Add <code className="bg-gray-100 px-2 py-1 rounded">?variant=A</code> or <code className="bg-gray-100 px-2 py-1 rounded">?variant=B</code> to force a variant</li>
              <li>• <strong>Console:</strong> Check browser console to see your assigned variant</li>
              <li>• <strong>GA4 Tracking:</strong> Events are sent to Google Analytics if configured</li>
            </ul>
          </CardContent>
        </Card>
        </>
        )}
      </div>
    </div>
  );
}
